import React, { useMemo, useState, useEffect } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  adminAutomationService, 
  AutomationWithUser 
} from '../../services/adminAutomationService';
import { platformEmailService } from '../../services/platformEmailService';
import AutomationSidebar from './AutomationSidebar';
import AutomationThreadList from './AutomationThreadList';
import AutomationFocusedView from './AutomationFocusedView';

export type AutomationFolder = 'all' | 'welcome' | 'promotional' | 'system' | 'success' | 'drafts' | 'archived' | 'sent';

interface AdminMailAutomationHubProps {
  defaultFolder?: AutomationFolder;
}

const AdminMailAutomationHub: React.FC<AdminMailAutomationHubProps> = ({ defaultFolder }) => {
  const { isDark } = useAdminTheme();
  const { showError } = useNotification();
  
  const [activeFolder, setActiveFolder] = useState<AutomationFolder>(defaultFolder || 'all');
  const [selectedAutomation, setSelectedAutomation] = useState<AutomationWithUser | null>(null);
  const [allAutomations, setAllAutomations] = useState<AutomationWithUser[]>([]);
  const [filteredAutomations, setFilteredAutomations] = useState<AutomationWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const isSystemEmailsPage = defaultFolder === 'system';
  const TRANSACTIONAL_SYSTEM_KEYS = [
    'user_verification',
    'password_reset',
    'founder_welcome',
    'sender_verification',
    'plan_activation',
    'double_optin',
  ];
  const isTransactionalSystemEmail = (a: any) => {
    const key = (a.systemKey || '').toLowerCase();
    const name = (a.name || '').toLowerCase();
    return (
      TRANSACTIONAL_SYSTEM_KEYS.includes(key) ||
      (a.category === 'system' && (
        name.includes('verify') ||
        name.includes('password reset') ||
        name.includes('welcome') ||
        name.includes('sender') ||
        name.includes('plan activation') ||
        name.includes('opt-in') ||
        name.includes('optin')
      ))
    );
  };
  const getSystemEmailTypeLabel = (a: any) => {
    const key = (a.systemKey || '').toLowerCase();
    if (key === 'user_verification') return 'Verify Email';
    if (key === 'password_reset') return 'Forgot Password';
    if (key === 'founder_welcome') return 'Welcome Email';
    if (key === 'sender_verification') return 'Sender Verification';
    if (key === 'plan_activation') return 'Plan Activation';
    if (key === 'double_optin') return 'Double Opt-in';
    return a.name || 'System Email';
  };

  const loadAutomations = async () => {
    try {
      setLoading(true);
      // Ensure defaults, but don't block page rendering if this fails.
      try {
        await platformEmailService.ensureDefaults();
      } catch (seedError) {
        console.warn('Failed to ensure default platform communications:', seedError);
      }

      // Fetch all automations, then filter in-memory for resilience.
      let data: AutomationWithUser[] = [];
      try {
        data = await adminAutomationService.getAllAutomations();
      } catch (primaryError) {
        console.warn('Primary automation fetch failed:', primaryError);
      }

      // If transactional system emails are missing, bootstrap and refetch once.
      const hasTransactionalSystemEmails = data.some((a) => isTransactionalSystemEmail(a as any));
      if (!hasTransactionalSystemEmails) {
        try {
          await platformEmailService.ensureDefaults();
          data = await adminAutomationService.getAllAutomations();
        } catch (bootstrapError) {
          console.warn('Failed to bootstrap transactional system emails:', bootstrapError);
        }
      }

      setAllAutomations(data);
      setSelectedAutomation((prev) => {
        if (!prev?.id) return prev;
        return data.find((item) => item.id === prev.id) || prev;
      });
    } catch (error) {
      console.error('Error loading platform communications:', error);
      showError('Failed to load platform messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAutomations();
  }, []);

  useEffect(() => {
    if (isSystemEmailsPage && activeFolder !== 'system') {
      setActiveFolder('system');
    }
  }, [isSystemEmailsPage, activeFolder]);

  useEffect(() => {
    let filtered = allAutomations;
    
    // Filter by folder
    if (activeFolder === 'welcome') {
      filtered = allAutomations.filter(a => (a as any).type === 'welcome');
    } else if (activeFolder === 'promotional') {
      filtered = allAutomations.filter(a => (a as any).type === 'promotional');
    } else if (activeFolder === 'system') {
      // Only true transactional system emails shown on /system-emails.
      filtered = allAutomations.filter((a: any) => isTransactionalSystemEmail(a));
    } else if (activeFolder === 'drafts') {
      filtered = allAutomations.filter(a => a.status === 'draft');
    } else if (activeFolder === 'sent') {
      filtered = allAutomations.filter(a => a.status === 'active');
    } else if (activeFolder === 'archived') {
      filtered = allAutomations.filter(a => a.status === 'archived');
    }

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(lowerSearch) || 
        a.description?.toLowerCase().includes(lowerSearch)
      );
    }

    setFilteredAutomations(filtered);
  }, [allAutomations, activeFolder, searchTerm]);

  const systemEmailTypeAutomations = useMemo(() => {
    if (activeFolder !== 'system') return [];
    const map = new Map<string, AutomationWithUser>();
    for (const item of filteredAutomations) {
      const key = ((item as any).systemKey || item.name || item.id || '').toLowerCase();
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, item);
        continue;
      }
      const existing = map.get(key)!;
      if (existing.status !== 'active' && item.status === 'active') {
        map.set(key, item);
      }
    }
    return Array.from(map.values());
  }, [activeFolder, filteredAutomations]);

  useEffect(() => {
    if (activeFolder !== 'system') return;
    const source = isSystemEmailsPage ? systemEmailTypeAutomations : filteredAutomations;
    if (source.length === 0) {
      setSelectedAutomation(null);
      return;
    }
    setSelectedAutomation((prev) => {
      if (prev?.id && source.some((item) => item.id === prev.id)) return prev;
      return source[0];
    });
  }, [activeFolder, filteredAutomations, systemEmailTypeAutomations, isSystemEmailsPage]);

  return (
    <div className={`flex h-[calc(100vh-73px)] overflow-hidden ${isDark ? 'bg-[#0f172a]' : 'bg-white'} font-sans`}>
      {/* Left Pane */}
      <div className={`w-64 flex-shrink-0 border-r ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
        {isSystemEmailsPage ? (
          <div className="h-full p-4">
            <div className={`text-[11px] font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
              System Email Types
            </div>
            <div className="space-y-1">
              {systemEmailTypeAutomations.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedAutomation(item)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition ${
                    selectedAutomation?.id === item.id
                      ? (isDark ? 'bg-blue-600/20 text-blue-300' : 'bg-[#E8F0FE] text-[#0B57D0]')
                      : (isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100')
                  }`}
                >
                  {getSystemEmailTypeLabel(item)}
                </button>
              ))}
              {!loading && systemEmailTypeAutomations.length === 0 && (
                <div className={`text-[12px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>No system email templates found.</div>
              )}
            </div>
          </div>
        ) : (
          <AutomationSidebar 
            activeFolder={activeFolder} 
            onFolderChange={setActiveFolder} 
          />
        )}
      </div>

      {/* Middle Pane - Message List (hidden for system emails tabbed view) */}
      {activeFolder !== 'system' && (
        <div className={`w-[400px] flex-shrink-0 border-r ${isDark ? 'border-slate-800' : 'border-slate-100'} flex flex-col ${isDark ? 'bg-[#111827]' : 'bg-white'}`}>
          {/* Search Header */}
          <div className={`p-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-100'} h-[64px] flex items-center`}>
            <div className="relative w-full">
              <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search platform messages"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-full border-none text-[14px] transition-all outline-none ${
                  isDark 
                    ? 'bg-slate-800 text-slate-100 placeholder-slate-500 focus:bg-slate-700' 
                    : 'bg-slate-100 text-slate-900 placeholder-slate-500 focus:bg-slate-200'
                }`}
              />
            </div>
          </div>
          
          {/* List Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
            <AutomationThreadList 
              automations={filteredAutomations}
              selectedId={selectedAutomation?.id || null}
              onSelect={setSelectedAutomation}
              loading={loading}
            />
          </div>
        </div>
      )}

      {/* Right Pane - Content View */}
      <div className={`flex-1 overflow-y-auto ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>
        <AutomationFocusedView 
          automation={selectedAutomation} 
          onUpdate={loadAutomations}
          showSystemEmailLibrary={!isSystemEmailsPage && activeFolder === 'system'}
          systemEmailAutomations={!isSystemEmailsPage && activeFolder === 'system' ? filteredAutomations : []}
          compactSystemLayout={isSystemEmailsPage}
        />
      </div>
    </div>
  );
};

export default AdminMailAutomationHub;
